import type { FunctionComponent } from "react";
import { graphql, usePaginationFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";
import type { authorListPaginationQuery } from "scmp-app/queries/__generated__/authorListPaginationQuery.graphql";
import type { authorListQuery$key } from "scmp-app/queries/__generated__/authorListQuery.graphql";

import { AuthorsContainer, Container, StyledEntityLink, StyledLoading } from "./styles";

export type Props = {
  reference: authorListQuery$key;
};

export const AuthorList: FunctionComponent<Props> = ({ reference: reference_, ...attributes }) => {
  const {
    data,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<authorListPaginationQuery, authorListQuery$key>(
    graphql`
      fragment authorListQuery on Query @refetchable(queryName: "authorListPaginationQuery") {
        authors(
          filter: {
            name: { operator: $nameOperator, value: $name }
            types: { operator: $typesOperator, value: $types }
            published: $published
          }
          orderBy: { field: NAME, direction: ASC }
          first: $first
          after: $after
          applicationId: $applicationId
        ) @connection(key: "authorListQuery__authors") {
          edges {
            node {
              __typename
              ...entityLink
              entityId
              name
            }
          }
        }
      }
    `,
    reference_,
  );

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(100),
  });

  if (data.authors?.edges?.length === 0) return null;
  return (
    <Container {...attributes}>
      <AuthorsContainer>
        {data.authors?.edges.filter(notEmpty).map(({ node }) => (
          <StyledEntityLink key={node.entityId} reference={node}>
            {node.name}
          </StyledEntityLink>
        ))}
      </AuthorsContainer>
      {hasNextPage && <StyledLoading ref={infiniteScrollTriggerReference} />}
    </Container>
  );
};

AuthorList.displayName = "AuthorList";
